/**
 * Is this tab running the code that is deployed?
 *
 * A stale browser tab looks exactly like a broken feature, and the sibling projects lost three
 * separate debugging sessions to that before anyone thought to check. The build id is compiled
 * in; `/build.json` is served fresh; if they disagree the app says so instead of letting the
 * owner debug a version that no longer exists.
 */

export const BUILD_ID: string = typeof __BUILD__ === 'string' ? __BUILD__ : 'dev';

export async function deployedBuildId(signal?: AbortSignal): Promise<string | null> {
  try {
    const response = await fetch(`/build.json?t=${Date.now()}`, { cache: 'no-store', signal });
    if (!response.ok) return null;
    const body = (await response.json()) as { build?: unknown };
    return typeof body.build === 'string' ? body.build : null;
  } catch {
    // Offline is the normal case for a local-first app, and it is not a stale tab.
    return null;
  }
}

export async function isStale(): Promise<boolean> {
  if (BUILD_ID === 'dev') return false;
  const deployed = await deployedBuildId();
  return deployed !== null && deployed !== BUILD_ID;
}

/** Check now, and again whenever the tab is brought back to the front. */
export function watchForNewBuild(onStale: () => void): () => void {
  let stopped = false;
  const check = () => {
    if (stopped || document.visibilityState !== 'visible') return;
    void isStale().then(stale => { if (stale && !stopped) onStale(); });
  };
  check();
  document.addEventListener('visibilitychange', check);
  return () => { stopped = true; document.removeEventListener('visibilitychange', check); };
}
